/* ══════════════════ Jungle Mode — Core State Management Hook ══════════════════ */

import { useCallback, useRef, useState } from 'react';
import { SimulationState, CELL_SIZE } from './simulationTypes';
import { tickSimulation, killJungleCamp } from './minionLogic';
import {
  JungleModeConfig,
  JungleModeStep,
  JungleModeSnapshot,
  DEFAULT_CONFIG,
} from './jungleModeTypes';
import {
  getRoute,
  getBasePosition,
  getChampLevel,
  calculateFreeTime,
  getAvailableRoute,
  straightLineWalkTime,
  computeFirstClearTiming,
} from './jungleModeRoutes';
import { getCampRewards } from './jungleCampRewards';
import type { RouteDirection } from './jungleModeTypes';

/* ── Fast-forward helper (same approach as useSimulation.fastForward) ── */
function fastForwardSim(sim: SimulationState, seconds: number): void {
  const step = 0.05;
  const steps = Math.ceil(seconds / step);
  for (let i = 0; i < steps; i++) {
    tickSimulation(sim, step);
  }
}

/* ══════════════════ Hook ══════════════════ */

export interface JungleModeControls {
  isActive: boolean;
  config: JungleModeConfig;
  steps: JungleModeStep[];
  playerPosition: { gx: number; gy: number };
  aiPosition: { gx: number; gy: number };
  playerLevel: number;
  aiLevel: number;
  animating: boolean;
  clearingCampId: string | null;
  goldPopup: { gx: number; gy: number; text: string } | null;
  /** True while the recall animation is playing */
  recalling: boolean;

  activate: (simRef: React.MutableRefObject<SimulationState>, resetSim: () => void) => void;
  deactivate: () => void;
  /** Phase 1: Take a snapshot before the walk animation starts. Returns false if step should be blocked. */
  takeSnapshot: (simRef: React.MutableRefObject<SimulationState>) => boolean;
  /** Get the clear time for a camp (depends on cycle and config) */
  getClearTime: (campId: string, simRef: React.MutableRefObject<SimulationState>) => number;
  /** Phase 2: Called after walk + clear animation. Camp kill, gold/xp, AI, table row. */
  completeStep: (
    campId: string,
    simRef: React.MutableRefObject<SimulationState>,
    walkTimeElapsed: number,
    clearTimeElapsed: number,
    /** Actual grid position where the champion ended up (kite position) */
    actualPos?: { gx: number; gy: number },
  ) => void;
  /** Called when recall channel finishes — teleports to base, adds table row */
  completeRecall: (simRef: React.MutableRefObject<SimulationState>, elapsedTime: number) => void;
  undo: (simRef: React.MutableRefObject<SimulationState>) => void;
  updateConfig: (partial: Partial<JungleModeConfig>) => void;
  setAnimating: (v: boolean) => void;
  setRecalling: (v: boolean) => void;
  setClearingCampId: (id: string | null) => void;
  campsInCurrentCycle: number;
  totalCampsInRoute: number;
  playerCycle: number;
  /** Derived average clear time per camp for first clear */
  firstClearAvg: number;
  /** Camp levels (campId → level 1-8) for display on map */
  campLevels: Record<string, number>;
}

export function useJungleMode(): JungleModeControls {
  const [isActive, setIsActive] = useState(false);
  const [config, setConfig] = useState<JungleModeConfig>({ ...DEFAULT_CONFIG });
  const [steps, setSteps] = useState<JungleModeStep[]>([]);
  const [playerPosition, setPlayerPosition] = useState({ gx: 0, gy: 0 });
  const [aiPosition, setAiPosition] = useState({ gx: 0, gy: 0 });
  const [animating, setAnimating] = useState(false);
  const [clearingCampId, setClearingCampId] = useState<string | null>(null);
  const [goldPopup, setGoldPopup] = useState<{ gx: number; gy: number; text: string } | null>(null);
  const [recalling, setRecalling] = useState(false);

  // Mutable refs for values that change during steps
  const playerRouteIndexRef = useRef(0);
  const aiRouteIndexRef = useRef(0);
  const playerCycleRef = useRef(1);
  const aiCycleRef = useRef(1);
  const cumulativeGoldRef = useRef(0);
  const cumulativeXPRef = useRef(0);
  const aiCumulativeGoldRef = useRef(0);
  const aiCumulativeXPRef = useRef(0);
  const historyStackRef = useRef<JungleModeSnapshot[]>([]);
  const playerRouteRef = useRef<string[]>([]);
  const aiRouteRef = useRef<string[]>([]);
  /** Derived average clear time per camp for first clear (computed from clearFinishTime) */
  const firstClearAvgRef = useRef(15);
  /** Camp kill counts — campId → number of times killed (level = kills + 1, capped at 8) */
  const campKillsRef = useRef<Record<string, number>>({});
  const [campLevels, setCampLevels] = useState<Record<string, number>>({});
  const stepsRef = useRef<JungleModeStep[]>([]);
  const playerPosRef = useRef({ gx: 0, gy: 0 });
  const aiPosRef = useRef({ gx: 0, gy: 0 });
  const configRef = useRef(config);
  configRef.current = config;
  stepsRef.current = steps;

  /* ── Resolve AI route direction ── */
  const resolveAIDirection = useCallback((cfg: JungleModeConfig): RouteDirection => {
    if (cfg.aiStrategy === 'mirror') return cfg.routeDirection;
    if (cfg.aiStrategy === 'random') {
      return Math.random() < 0.5 ? 'top-to-bot' : 'bot-to-top';
    }
    return cfg.aiStrategy as RouteDirection;
  }, []);

  /* ── Activate ── */
  const activate = useCallback(
    (simRef: React.MutableRefObject<SimulationState>, resetSim: () => void) => {
      resetSim();

      const cfg = configRef.current;
      const aiSide = cfg.playerSide === 'blue' ? 'red' : 'blue';

      const pRoute = getRoute(cfg.playerSide, cfg.routeDirection);
      const aiDir = resolveAIDirection(cfg);
      const aRoute = getRoute(aiSide, aiDir);

      playerRouteRef.current = pRoute;
      aiRouteRef.current = aRoute;
      playerRouteIndexRef.current = 0;
      aiRouteIndexRef.current = 0;
      playerCycleRef.current = 1;
      aiCycleRef.current = 1;
      cumulativeGoldRef.current = 0;
      cumulativeXPRef.current = 0;
      aiCumulativeGoldRef.current = 0;
      aiCumulativeXPRef.current = 0;
      historyStackRef.current = [];
      campKillsRef.current = {};
      setCampLevels({});

      // Fast-forward to configured start time (+ small buffer to avoid floating-point
      // landing at 54.999... instead of 55.0, which would leave camps unspawned)
      fastForwardSim(simRef.current, cfg.startTime + 0.1);

      // Compute derived first-clear avg time per camp from clearFinishTime
      const timing = computeFirstClearTiming(pRoute, simRef.current.jungleCamps, cfg);
      firstClearAvgRef.current = timing.avgClearTime;

      // Position junglers slightly offset from their first camp (sideways, toward
      // the map centre to avoid walls) so clicking the camp triggers a short walk.
      const SPAWN_OFFSET = 8; // grid cells
      const pCamp = simRef.current.jungleCamps.find(c => c.id === pRoute[0]);
      const aCamp = simRef.current.jungleCamps.find(c => c.id === aRoute[0]);
      const MAP_CENTRE = 256; // centre of 512×512 grid

      let pPos: { gx: number; gy: number };
      if (pCamp) {
        // Offset toward map centre (avoids walls at map edges)
        const dx = MAP_CENTRE - pCamp.gx;
        const dy = MAP_CENTRE - pCamp.gy;
        const dist = Math.hypot(dx, dy);
        const nx = dist > 0 ? dx / dist : 1;
        const ny = dist > 0 ? dy / dist : 0;
        pPos = { gx: pCamp.gx + nx * SPAWN_OFFSET, gy: pCamp.gy + ny * SPAWN_OFFSET };
      } else {
        pPos = getBasePosition(cfg.playerSide);
      }

      let aPos: { gx: number; gy: number };
      if (aCamp) {
        const dx = MAP_CENTRE - aCamp.gx;
        const dy = MAP_CENTRE - aCamp.gy;
        const dist = Math.hypot(dx, dy);
        const nx = dist > 0 ? dx / dist : 1;
        const ny = dist > 0 ? dy / dist : 0;
        aPos = { gx: aCamp.gx + nx * SPAWN_OFFSET, gy: aCamp.gy + ny * SPAWN_OFFSET };
      } else {
        aPos = getBasePosition(aiSide);
      }

      setPlayerPosition(pPos);
      setAiPosition(aPos);
      playerPosRef.current = pPos;
      aiPosRef.current = aPos;

      setSteps([]);
      stepsRef.current = [];
      setIsActive(true);
      setAnimating(false);
      setClearingCampId(null);
      setGoldPopup(null);
    },
    [resolveAIDirection],
  );

  /* ── Deactivate ── */
  const deactivate = useCallback(() => {
    setIsActive(false);
    setSteps([]);
    setAnimating(false);
    setClearingCampId(null);
    setGoldPopup(null);
  }, []);

  /* ── Phase 1: Take snapshot before walk animation ── */
  const takeSnapshot = useCallback(
    (simRef: React.MutableRefObject<SimulationState>): boolean => {
      if (animating) return false;

      const sim = simRef.current;
      const snapshot: JungleModeSnapshot = {
        gameTime: sim.gameTime,
        playerPosition: { ...playerPosRef.current },
        aiPosition: { ...aiPosRef.current },
        playerRouteIndex: playerRouteIndexRef.current,
        aiRouteIndex: aiRouteIndexRef.current,
        playerCycle: playerCycleRef.current,
        aiCycle: aiCycleRef.current,
        cumulativeGold: cumulativeGoldRef.current,
        cumulativeXP: cumulativeXPRef.current,
        aiCumulativeGold: aiCumulativeGoldRef.current,
        aiCumulativeXP: aiCumulativeXPRef.current,
        campStates: sim.jungleCamps.map(c => ({
          id: c.id,
          alive: c.alive,
          nextSpawnAt: c.nextSpawnAt,
          killedAt: c.killedAt,
        })),
        steps: [...stepsRef.current],
        waveCounter: { ...sim.waveCounter },
        nextWaveTime: { ...sim.nextWaveTime },
        nextMinionId: sim.nextMinionId,
        campKills: { ...campKillsRef.current },
      };
      historyStackRef.current.push(snapshot);
      setAnimating(true);
      return true;
    },
    [animating],
  );

  /* ── Get clear time for a camp ── */
  const getClearTime = useCallback(
    (campId: string, simRef: React.MutableRefObject<SimulationState>): number => {
      const camp = simRef.current.jungleCamps.find(c => c.id === campId);
      if (!camp || !camp.alive) return 0;
      const cfg = configRef.current;
      // Check if re-clearing → would bump to next cycle
      const wouldBeCycle = stepsRef.current.some(
        s => !s.isFreeTimeRow && s.cycleNumber === playerCycleRef.current && s.campId === campId,
      ) ? playerCycleRef.current + 1 : playerCycleRef.current;
      return wouldBeCycle === 1 ? firstClearAvgRef.current : cfg.subsequentClearTime;
    },
    [],
  );

  /* ── Phase 2: Complete step after walk + clear animations ── */
  const completeStep = useCallback(
    (
      campId: string,
      simRef: React.MutableRefObject<SimulationState>,
      walkTimeElapsed: number,
      clearTimeElapsed: number,
      actualPos?: { gx: number; gy: number },
    ) => {
      const sim = simRef.current;
      const cfg = configRef.current;
      const camp = sim.jungleCamps.find(c => c.id === campId);
      if (!camp) { setAnimating(false); return; }

      // Camp was alive when we started clearing (clearTimeElapsed > 0 means it was alive)
      const wasAlive = clearTimeElapsed > 0;

      // Update player position ref to where the champion actually is (kite position)
      if (actualPos) {
        playerPosRef.current = actualPos;
        setPlayerPosition(actualPos);
      }

      // Dead camp → just a walk, no table row, no kill
      if (!wasAlive) {
        setClearingCampId(null);
        setAnimating(false);
        return;
      }

      // Arrival time = current game time minus the clear time that just animated
      const gameTimeArrive = sim.gameTime - clearTimeElapsed;

      // Calculate idle time — how long the camp sat alive before we arrived
      let idleTime = 0;
      const spawnedAt = camp.killedAt != null
        ? camp.killedAt + camp.respawnTime
        : camp.initialSpawn;
      idleTime = Math.max(0, gameTimeArrive - spawnedAt);

      // Check if this camp was already cleared in the current cycle → auto-start new cycle
      const alreadyClearedInCycle = stepsRef.current.some(
        s => !s.isFreeTimeRow && s.cycleNumber === playerCycleRef.current && s.campId === campId,
      );
      if (alreadyClearedInCycle) {
        playerCycleRef.current++;
        playerRouteIndexRef.current = 0;
      }

      // Kill camp and get rewards based on camp level
      const simCamp = sim.jungleCamps.find(c => c.id === campId);
      if (simCamp && simCamp.alive) {
        killJungleCamp(sim, campId);
      }
      // Camp level = number of previous kills + 1 (capped at 8)
      const prevKills = campKillsRef.current[campId] ?? 0;
      const campLevel = Math.min(8, prevKills + 1);
      const rewards = getCampRewards(campId, campLevel);
      const gold = rewards.gold;
      const xp = rewards.xp;

      // Track the kill
      campKillsRef.current[campId] = prevKills + 1;
      setCampLevels(prev => ({ ...prev, [campId]: Math.min(8, prevKills + 2) })); // next level

      cumulativeGoldRef.current += gold;
      cumulativeXPRef.current += xp;
      const level = getChampLevel(cumulativeXPRef.current);

      // Advance AI
      const totalElapsed = walkTimeElapsed + clearTimeElapsed;
      advanceAI(totalElapsed, sim, cfg);

      // Update route index
      const route = playerRouteRef.current;
      if (route[playerRouteIndexRef.current] === campId) {
        playerRouteIndexRef.current++;
      }

      // Build step row
      const stepNumber = stepsRef.current.filter(s => !s.isFreeTimeRow).length + 1;
      const newStep: JungleModeStep = {
        stepNumber,
        campId,
        campName: camp.name,
        walkTime: Math.round(walkTimeElapsed),
        clearTime: Math.round(clearTimeElapsed),
        gameTimeArrive,
        gameTimeAfter: sim.gameTime,
        idleTime: Math.round(idleTime),
        gold,
        xp,
        cumulativeGold: cumulativeGoldRef.current,
        cumulativeXP: cumulativeXPRef.current,
        level,
        cycleNumber: playerCycleRef.current,
        isFreeTimeRow: false,
      };

      const newSteps = [...stepsRef.current, newStep];
      setSteps(newSteps);
      stepsRef.current = newSteps;

      // Trigger gold popup
      if (gold > 0) {
        setGoldPopup({ gx: camp.gx, gy: camp.gy, text: `+${gold}g` });
        setTimeout(() => setGoldPopup(null), 1200);
      }

      // Clear the clearing animation (it was shown during channel)
      setClearingCampId(null);
      setAnimating(false);
    },
    [],
  );

  /* ── Complete recall (called after channel finishes in the frame loop) ── */
  const completeRecall = useCallback(
    (simRef: React.MutableRefObject<SimulationState>, elapsedTime: number) => {
      const sim = simRef.current;
      const cfg = configRef.current;

      // Teleport to base
      const base = getBasePosition(cfg.playerSide);
      playerPosRef.current = base;
      setPlayerPosition(base);

      // AI also advances one camp
      advanceAI(elapsedTime, sim, cfg);

      // Add a row to the table
      const level = getChampLevel(cumulativeXPRef.current);
      // Add recall row
      const recallStep: JungleModeStep = {
        stepNumber: stepsRef.current.filter(s => !s.isFreeTimeRow).length + 1,
        campId: 'recall',
        campName: 'Recall',
        walkTime: 0,
        clearTime: Math.round(elapsedTime),
        gameTimeArrive: sim.gameTime - elapsedTime,
        gameTimeAfter: sim.gameTime,
        idleTime: 0,
        gold: 0,
        xp: 0,
        cumulativeGold: cumulativeGoldRef.current,
        cumulativeXP: cumulativeXPRef.current,
        level,
        cycleNumber: playerCycleRef.current,
        isFreeTimeRow: false,
      };

      const newSteps = [...stepsRef.current, recallStep];

      // Add free time summary for the completed cycle
      const route = playerRouteRef.current;
      const { freeTime, detail, nextCampId } = calculateFreeTime(
        route, sim.jungleCamps, sim.gameTime, cfg,
      );

      const freeTimeStep: JungleModeStep = {
        stepNumber: 0,
        campId: nextCampId,
        campName: `Cycle ${playerCycleRef.current} Complete`,
        walkTime: 0,
        clearTime: 0,
        gameTimeArrive: sim.gameTime,
        gameTimeAfter: sim.gameTime,
        idleTime: 0,
        gold: 0,
        xp: 0,
        cumulativeGold: cumulativeGoldRef.current,
        cumulativeXP: cumulativeXPRef.current,
        level,
        cycleNumber: playerCycleRef.current,
        isFreeTimeRow: true,
        freeTime,
        freeTimeDetail: detail,
      };
      newSteps.push(freeTimeStep);

      // Basing ends the current cycle
      playerCycleRef.current++;
      playerRouteIndexRef.current = 0;

      setSteps(newSteps);
      stepsRef.current = newSteps;
      setAnimating(false);
      setRecalling(false);
    },
    [],
  );

  /* ── Advance AI jungler — one camp per player step ── */
  function advanceAI(
    _elapsed: number,
    sim: SimulationState,
    cfg: JungleModeConfig,
  ): void {
    const aiSide = cfg.playerSide === 'blue' ? 'red' : 'blue';
    const route = aiRouteRef.current;

    if (aiRouteIndexRef.current >= route.length) {
      // AI finished its cycle — start next
      aiCycleRef.current++;
      aiRouteIndexRef.current = 0;
    }

    if (aiRouteIndexRef.current >= route.length) return;

    const campId = route[aiRouteIndexRef.current];
    const camp = sim.jungleCamps.find(c => c.id === campId);
    if (!camp) {
      aiRouteIndexRef.current++;
      aiPosRef.current = aiPosRef.current;
      setAiPosition(aiPosRef.current);
      return;
    }

    // Move AI to this camp and clear it
    const pos = { gx: camp.gx, gy: camp.gy };

    if (camp.alive) {
      killJungleCamp(sim, campId);
      // AI camps also level up — track separately (use same campKills for simplicity)
      const aiPrevKills = campKillsRef.current[campId] ?? 0;
      const aiCampLevel = Math.min(8, aiPrevKills + 1);
      const aiRewards = getCampRewards(campId, aiCampLevel);
      campKillsRef.current[campId] = aiPrevKills + 1;
      aiCumulativeGoldRef.current += aiRewards.gold;
      aiCumulativeXPRef.current += aiRewards.xp;
    }

    aiRouteIndexRef.current++;
    aiPosRef.current = pos;
    setAiPosition(pos);
  }

  /* ── Undo ── */
  const undo = useCallback(
    (simRef: React.MutableRefObject<SimulationState>) => {
      const stack = historyStackRef.current;
      if (stack.length === 0) return;

      const snapshot = stack.pop()!;

      // Restore sim state from lightweight snapshot (no full clone needed)
      const sim = simRef.current;
      sim.gameTime = snapshot.gameTime;
      sim.waveCounter = { ...snapshot.waveCounter };
      sim.nextWaveTime = { ...snapshot.nextWaveTime };
      sim.nextMinionId = snapshot.nextMinionId;
      // Restore camp states
      for (const saved of snapshot.campStates) {
        const camp = sim.jungleCamps.find(c => c.id === saved.id);
        if (camp) {
          camp.alive = saved.alive;
          camp.nextSpawnAt = saved.nextSpawnAt;
          camp.killedAt = saved.killedAt;
        }
      }

      campKillsRef.current = { ...snapshot.campKills };
      setCampLevels(Object.fromEntries(
        Object.entries(snapshot.campKills).map(([id, kills]) => [id, Math.min(8, kills + 1)]),
      ));

      playerPosRef.current = snapshot.playerPosition;
      aiPosRef.current = snapshot.aiPosition;
      setPlayerPosition(snapshot.playerPosition);
      setAiPosition(snapshot.aiPosition);

      playerRouteIndexRef.current = snapshot.playerRouteIndex;
      aiRouteIndexRef.current = snapshot.aiRouteIndex;
      playerCycleRef.current = snapshot.playerCycle;
      aiCycleRef.current = snapshot.aiCycle;
      cumulativeGoldRef.current = snapshot.cumulativeGold;
      cumulativeXPRef.current = snapshot.cumulativeXP;
      aiCumulativeGoldRef.current = snapshot.aiCumulativeGold;
      aiCumulativeXPRef.current = snapshot.aiCumulativeXP;

      setSteps(snapshot.steps);
      stepsRef.current = snapshot.steps;

      setAnimating(false);
      setClearingCampId(null);
      setGoldPopup(null);
    },
    [],
  );

  /* ── Update config ── */
  const updateConfig = useCallback((partial: Partial<JungleModeConfig>) => {
    setConfig(prev => ({ ...prev, ...partial }));
  }, []);

  const currentRoute = playerRouteRef.current;
  const campsInCurrentCycle = steps.filter(
    s => !s.isFreeTimeRow && s.cycleNumber === playerCycleRef.current,
  ).length;

  return {
    isActive,
    config,
    steps,
    playerPosition,
    aiPosition,
    playerLevel: getChampLevel(cumulativeXPRef.current),
    aiLevel: getChampLevel(aiCumulativeXPRef.current),
    animating,
    clearingCampId,
    goldPopup,
    recalling,
    activate,
    deactivate,
    takeSnapshot,
    getClearTime,
    completeStep,
    completeRecall,
    undo,
    updateConfig,
    setAnimating,
    setRecalling,
    setClearingCampId,
    campsInCurrentCycle,
    totalCampsInRoute: currentRoute.length,
    playerCycle: playerCycleRef.current,
    firstClearAvg: Math.round(firstClearAvgRef.current * 10) / 10,
    campLevels,
  };
}
